import { useMemo } from 'react'
import { Vehicle } from '@otw/models'
import { useInventory } from '@/hooks/inventory/useInventory'

const PACKAGE_WINDOW_DAYS = 30

export type PaymentType = 'finance' | 'cash'

export interface ClientActivityPackage {
  /** First sold_date in this package (ISO string) */
  soldDateStart: string
  /** Last sold_date in this package (ISO string) */
  soldDateEnd: string
  vehicles: Vehicle[]
  /** Sum of sale prices for this package */
  totalSpent: number
  financedCount: number
  cashCount: number
}

export interface ClientActivityClient {
  id: string
  name: string
  packages: ClientActivityPackage[]
  /** All vehicles sold to this client (flattened) */
  vehicles: Vehicle[]
  /** Total spent across all packages */
  totalSpent: number
  /** Total number of trucks */
  truckCount: number
}

/** Total spent = optic_list_price + warranty customer cost + shipping customer cost. */
function getVehicleSalePrice(v: Vehicle): number {
  const vehicle = Number(v.optic_list_price) || 0
  const warranty = Number(v.warranty?.customer_cost) || 0
  const shipping = Number(v.shipping?.customer_shipping_cost) || 0
  return vehicle + warranty + shipping
}

export function isFinanced(vehicle: Vehicle): boolean {
  if (!vehicle.finance) return false
  const pt = (vehicle.finance.payment_type || '').toLowerCase()
  if (pt === 'cash') return false
  return true
}

function groupIntoPackages(vehicles: Vehicle[]): ClientActivityPackage[] {
  if (vehicles.length === 0) return []

  const sorted = [...vehicles].sort((a, b) => {
    const da = a.sold_date ? new Date(a.sold_date).getTime() : 0
    const db = b.sold_date ? new Date(b.sold_date).getTime() : 0
    return da - db
  })

  const packages: ClientActivityPackage[] = []
  let current: Vehicle[] = []
  let packageStart: string | null = null

  const flushPackage = (endDate: string) => {
    if (current.length === 0) return
    const start = packageStart || current[0]?.sold_date || ''
    const totalSpent = current.reduce((sum, v) => sum + getVehicleSalePrice(v), 0)
    const financedCount = current.filter(isFinanced).length
    const cashCount = current.length - financedCount
    packages.push({
      soldDateStart: start,
      soldDateEnd: endDate,
      vehicles: [...current],
      totalSpent,
      financedCount,
      cashCount,
    })
    current = []
    packageStart = null
  }

  for (const v of sorted) {
    const soldDate = v.sold_date || ''
    const soldTime = soldDate ? new Date(soldDate).getTime() : 0

    if (current.length === 0) {
      current.push(v)
      packageStart = soldDate
      continue
    }

    const firstInPackage = packageStart ? new Date(packageStart).getTime() : soldTime
    const windowMs = PACKAGE_WINDOW_DAYS * 24 * 60 * 60 * 1000
    if (soldTime - firstInPackage <= windowMs) {
      current.push(v)
    } else {
      flushPackage(current[current.length - 1]?.sold_date || soldDate)
      current.push(v)
      packageStart = soldDate
    }
  }

  const lastSold = current[current.length - 1]?.sold_date
  if (current.length > 0 && lastSold) {
    flushPackage(lastSold)
  }

  return packages
}

export type DateRangeOption = 'last_year' | 'last_month' | 'all'

function filterVehiclesByDateRange(vehicles: Vehicle[], dateRange: DateRangeOption): Vehicle[] {
  if (dateRange === 'all' || !vehicles.length) return vehicles
  const now = Date.now()
  const oneMonthMs = 30 * 24 * 60 * 60 * 1000
  const oneYearMs = 365 * 24 * 60 * 60 * 1000
  const cutoff =
    dateRange === 'last_month' ? now - oneMonthMs : dateRange === 'last_year' ? now - oneYearMs : 0
  if (cutoff === 0) return vehicles
  return vehicles.filter((v) => {
    const t = v.sold_date ? new Date(v.sold_date).getTime() : 0
    return t >= cutoff
  })
}

export interface UseClientActivityOptions {
  dateRange?: DateRangeOption
}

export function useClientActivity(options?: UseClientActivityOptions) {
  const dateRange = options?.dateRange ?? 'last_year'
  const { vehicles = [], isLoading, isError, error, refetchVehicles } = useInventory('master_inventory')

  const soldVehicles = useMemo(() => {
    if (!vehicles.length) return []
    return vehicles.filter(
      (v) =>
        (v.status === 'Sold' || v.status === 'Sold elsewhere') &&
        v.sold_to_user != null &&
        v.sold_date,
    )
  }, [vehicles])

  const dateFilteredVehicles = useMemo(
    () => filterVehiclesByDateRange(soldVehicles, dateRange),
    [soldVehicles, dateRange],
  )

  const clients = useMemo((): ClientActivityClient[] => {
    if (!dateFilteredVehicles.length) return []

    const byClient = new Map<string, Vehicle[]>()
    for (const v of dateFilteredVehicles) {
      const client = v.sold_to_user!
      const key = (client.name || client.id || 'unknown').trim().toLowerCase()
      if (!byClient.has(key)) {
        byClient.set(key, [])
      }
      byClient.get(key)!.push(v)
    }

    const toTitleCase = (s: string) =>
      (s || '')
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())

    const result: ClientActivityClient[] = []
    byClient.forEach((clientVehicles, key) => {
      const first = clientVehicles[0]
      const soldTo = first?.sold_to_user
      const rawName = soldTo?.name || key
      const name = toTitleCase(rawName)
      const id = key
      const packages = groupIntoPackages(clientVehicles)
      const totalSpent = packages.reduce((sum, p) => sum + p.totalSpent, 0)
      result.push({
        id,
        name,
        packages,
        vehicles: clientVehicles,
        totalSpent,
        truckCount: clientVehicles.length,
      })
    })

    // Sort clients by most recent sale first (by latest sold_date across vehicles)
    result.sort((a, b) => {
      const aMax = Math.max(
        ...a.vehicles.map((v) => (v.sold_date ? new Date(v.sold_date).getTime() : 0)),
      )
      const bMax = Math.max(
        ...b.vehicles.map((v) => (v.sold_date ? new Date(v.sold_date).getTime() : 0)),
      )
      return bMax - aMax
    })

    return result
  }, [dateFilteredVehicles])

  return {
    clients,
    soldVehicles,
    isLoading,
    isError,
    error,
    refetch: refetchVehicles,
    isFinanced,
    getVehicleSalePrice,
  }
}
